import {systemDateFormat} from '@lib/utils';
import {createSlice, isAnyOf} from '@reduxjs/toolkit';
import format from 'date-fns/format';
import isToday from 'date-fns/isToday';
import parseISO from 'date-fns/parseISO';
import {ReservationMember, WorkspaceReservationStatus} from '../entities';
import {
  loadLastSelectedWorkspaceReservationLocation,
  loadMyWorkspaceReservations,
  registerWorkspaceReservation,
  removeWorkspaceReservation,
  saveLastSelectedWorkspaceReservationLocation,
  updateWorkspaceReservationStatus,
  searchGroupWorkspaceInvitees,
  updateWorkspaceReservation,
  loadWorkspaceReservationById,
  createGroupWorkspaceReservation,
  cancelWorkspaceReservation,
  registerWorkspaceReservationWithPreferredLocation,
} from './actions';
import {workspaceReservationsAdapter} from './adapters';
import {initialState} from './initialState';
import {
  getWorkspaceReservationSpaceNodeId,
  updateOccupationCountForNodeWhenWorkspaceIsUpdated,
  updateCountForNodeWhenWorkspaceReservationIsRemoved,
  getWorkspaceReservationByGroupReservationId,
} from './utils';

const slice = createSlice({
  name: 'workspaceReservations',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadMyWorkspaceReservations.fulfilled, (state, action) => {
        const {workspaceReservationCountPerNodeOnDate, workspaceReservations} = action.payload;
        workspaceReservationsAdapter.setAll(state.workspaceReservations, workspaceReservations ?? []);
        state.workspaceReservationCountPerNodeOnDate = workspaceReservationCountPerNodeOnDate;
        state.isLoading = false;
      })
      .addCase(registerWorkspaceReservation.fulfilled, (state, action) => {
        const workspaceReservation = action.payload;

        workspaceReservationsAdapter.addOne(state.workspaceReservations, workspaceReservation);

        // Updates the registration count for that node
        const date = format(parseISO(workspaceReservation.startDate), systemDateFormat);
        const selectedSpaceNodeId = getWorkspaceReservationSpaceNodeId(workspaceReservation);
        const nodesOnDate = state.workspaceReservationCountPerNodeOnDate[date];
        if (nodesOnDate) {
          nodesOnDate[selectedSpaceNodeId] = (nodesOnDate[selectedSpaceNodeId] ?? 0) + 1;
        }

        state.isLoading = false;
        state.isCreating = false;
      })
      .addCase(registerWorkspaceReservationWithPreferredLocation.fulfilled, (state, action) => {
        const workspaceReservation = action.payload;

        workspaceReservationsAdapter.addOne(state.workspaceReservations, workspaceReservation);

        // Updates the registration count for that node
        const date = format(parseISO(workspaceReservation.startDate), systemDateFormat);
        const selectedSpaceNodeId = getWorkspaceReservationSpaceNodeId(workspaceReservation);
        const nodesOnDate = state.workspaceReservationCountPerNodeOnDate[date];
        if (nodesOnDate) {
          nodesOnDate[selectedSpaceNodeId] = (nodesOnDate[selectedSpaceNodeId] ?? 0) + 1;
        }

        state.isLoading = false;
        state.isCreating = false;
      })
      .addCase(updateWorkspaceReservation.pending, (state, action) => {
        state.isLoading = true;
        state.updates[action.meta.arg.workspaceReservationId] = true;
      })
      .addCase(updateWorkspaceReservation.fulfilled, (state, action) => {
        const updatedWorkspaceReservation = action.payload;
        const oldWorkspaceReservation = state.workspaceReservations.entities[updatedWorkspaceReservation.id];

        workspaceReservationsAdapter.updateOne(state.workspaceReservations, {
          id: updatedWorkspaceReservation.id,
          changes: updatedWorkspaceReservation,
        });

        updateOccupationCountForNodeWhenWorkspaceIsUpdated(oldWorkspaceReservation, updatedWorkspaceReservation, state);

        state.isLoading = false;
        state.updates[action.meta.arg.workspaceReservationId] = false;
      })
      .addCase(updateWorkspaceReservation.rejected, (state, action) => {
        state.isLoading = false;
        state.updates[action.meta.arg.workspaceReservationId] = false;
      })
      .addCase(removeWorkspaceReservation.pending, (state, action) => {
        state.deletions[action.meta.arg.id] = true;
        const workspaceReservationDate = action.meta.arg.startDate;
        const isWorkspaceReservationDateToday = isToday(parseISO(workspaceReservationDate));
        if (isWorkspaceReservationDateToday) {
          state.isDeletingTodayRegistration = true;
        }
        state.isDeletingRegistration = true;
      })
      .addCase(removeWorkspaceReservation.fulfilled, (state, action) => {
        const workspaceReservation = action.meta.arg;
        const userId = action.payload;

        updateCountForNodeWhenWorkspaceReservationIsRemoved(state, workspaceReservation, userId);
        workspaceReservationsAdapter.removeOne(state.workspaceReservations, workspaceReservation.id);

        state.deletions[action.meta.arg.id] = false;
        state.isDeletingRegistration = false;
        state.isDeletingTodayRegistration = false;
      })
      .addCase(removeWorkspaceReservation.rejected, (state, action) => {
        state.deletions[action.meta.arg.id] = false;
        state.isDeletingRegistration = false;
        state.isDeletingTodayRegistration = false;
      })
      .addCase(loadLastSelectedWorkspaceReservationLocation.fulfilled, (state, {payload}) => {
        if (payload) {
          state.lastSelectedLocation = payload;
        }
        state.isLastSelectedLocationLoaded = true;
      })
      .addCase(saveLastSelectedWorkspaceReservationLocation.fulfilled, (state, action) => {
        state.lastSelectedLocation = action.payload;
        state.isLastSelectedLocationLoaded = true;
      })
      .addCase(updateWorkspaceReservationStatus.fulfilled, (state, action) => {
        const {reservationId, groupReservationId, status, userId} = action.payload;
        const {currentWorkspaceReservationId} = action.meta.arg;
        const updatedWorkspaceReservation = getWorkspaceReservationByGroupReservationId(
          Object.values(state.workspaceReservations.entities),
          groupReservationId,
        );

        if (!updatedWorkspaceReservation) {
          return;
        }

        if (status === 'Confirmed') {
          const {currentMember, updatedMembers} = updatedWorkspaceReservation.members.reduce(
            (acc, member) => {
              if (member.reservationId === reservationId) {
                return {
                  ...acc,
                  currentMember: {...member, status: 'Confirmed' as WorkspaceReservationStatus},
                  updatedMembers: [
                    ...acc.updatedMembers,
                    {...member, status: 'Confirmed' as WorkspaceReservationStatus},
                  ],
                };
              }
              return {...acc, updatedMembers: [...acc.updatedMembers, member]};
            },
            {
              updatedMembers: [] as ReservationMember[],
              currentMember: {} as ReservationMember,
            },
          );
          workspaceReservationsAdapter.updateOne(state.workspaceReservations, {
            id: updatedWorkspaceReservation.id,
            changes: {
              currentMember,
              status: status,
              members: updatedMembers,
            },
          });

          if (currentWorkspaceReservationId) {
            const currentWorkspaceReservation = state.workspaceReservations.entities[currentWorkspaceReservationId];
            updateCountForNodeWhenWorkspaceReservationIsRemoved(state, currentWorkspaceReservation, userId);
            workspaceReservationsAdapter.removeOne(state.workspaceReservations, currentWorkspaceReservationId);
            state.updates[updatedWorkspaceReservation.id] = false;
          }
          return;
        }
        const declinedReservation = state.workspaceReservations.entities[updatedWorkspaceReservation.id];
        workspaceReservationsAdapter.removeOne(state.workspaceReservations, updatedWorkspaceReservation.id);
        updateCountForNodeWhenWorkspaceReservationIsRemoved(state, declinedReservation, userId);
        state.updates[updatedWorkspaceReservation.id] = false;
      })
      .addCase(updateWorkspaceReservationStatus.rejected, (state, action) => {
        const {groupReservationId} = action.meta.arg;
        const updatedWorkspaceReservation = getWorkspaceReservationByGroupReservationId(
          Object.values(state.workspaceReservations.entities),
          groupReservationId,
        );

        if (!updatedWorkspaceReservation) {
          return;
        }
        state.updates[updatedWorkspaceReservation.id] = false;
      })
      .addCase(updateWorkspaceReservationStatus.pending, (state, action) => {
        const {groupReservationId} = action.meta.arg;
        const updatedWorkspaceReservation = getWorkspaceReservationByGroupReservationId(
          Object.values(state.workspaceReservations.entities),
          groupReservationId,
        );

        if (!updatedWorkspaceReservation) {
          return;
        }
        state.updates[updatedWorkspaceReservation.id] = true;
      })
      .addCase(searchGroupWorkspaceInvitees.fulfilled, (state, action) => {
        state.searchedInvitees = action.payload;
        state.inviteesSearchStatus = 'Loaded';
      })
      .addCase(searchGroupWorkspaceInvitees.pending, (state) => {
        state.inviteesSearchStatus = 'Loading';
      })
      .addCase(searchGroupWorkspaceInvitees.rejected, (state) => {
        state.inviteesSearchStatus = 'Failed';
      })
      .addCase(loadWorkspaceReservationById.fulfilled, (state, action) => {
        const workspaceReservation = action.payload;
        const oldWorkspaceReservation = state.workspaceReservations.entities[workspaceReservation.id];
        workspaceReservationsAdapter.setOne(state.workspaceReservations, workspaceReservation);
        updateOccupationCountForNodeWhenWorkspaceIsUpdated(oldWorkspaceReservation, workspaceReservation, state);

        state.isLoading = false;
      })
      .addCase(createGroupWorkspaceReservation.fulfilled, (state) => {
        //TODO This case should be removed once group-workspace-reservation and workspace-reservation POST endpoint are merged
        state.isLoading = false;
        state.isCreating = false;
      })
      .addCase(cancelWorkspaceReservation, (state, action) => {
        workspaceReservationsAdapter.removeOne(state.workspaceReservations, action.payload);
      })
      .addMatcher(
        isAnyOf(
          createGroupWorkspaceReservation.pending,
          registerWorkspaceReservation.pending,
          registerWorkspaceReservationWithPreferredLocation.pending,
        ),
        (state) => {
          state.isLoading = true;
          state.isCreating = true;
        },
      )
      .addMatcher(
        isAnyOf(
          registerWorkspaceReservation.rejected,
          createGroupWorkspaceReservation.rejected,
          registerWorkspaceReservationWithPreferredLocation.rejected,
        ),
        (state) => {
          state.isLoading = false;
          state.isCreating = false;
        },
      )
      .addMatcher(isAnyOf(loadMyWorkspaceReservations.pending, loadWorkspaceReservationById.pending), (state) => {
        state.isLoading = true;
      })
      .addMatcher(isAnyOf(loadMyWorkspaceReservations.rejected, loadWorkspaceReservationById.rejected), (state) => {
        state.isLoading = false;
      });
  },
});

export const {reducer: workspaceReservationsReducer} = slice;
